import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/BookReader.css';

export default function DrawingToolbar({
  activeTool,
  onToolSelect,
  currentColor,
  setCurrentColor,
  quotePicking = false,

  // NEW (all optional; BookReader can pass these):
  penWidth = 2,
  highlightWidth = 15,
  eraserWidth = 12,
  eraserStyle = 'erase',               // 'erase' | 'strike'
  highlightMode = 'freehand',          // 'freehand' | 'text'
  onPenWidthChange,
  onHighlightWidthChange,
  onEraserConfigChange,                // ({ style?, width? })
  onHighlightModeChange,               // (mode)
  onPaletteUpdate,                     // (newPaletteArray)

  // Undo/Redo controls
  onUndo = () => {},
  onRedo = () => {},
  canUndo = false,
  canRedo = false,
}) {
  // ----- primary tool buttons
  const tools = [
    { id: 'eraser',   label: 'Eraser',    icon: 'auto_fix_off',      title: 'Erase or Strike' },
    { id: 'pen',      label: 'Pen',       icon: 'edit',              title: 'Draw with pen' },
    { id: 'highlight',label: 'Highlight', icon: 'format_color_text',         title: 'Highlight' },
  ];

  // ----- palette (stateful so you can swap colors)
  const [palette, setPalette] = useState([
    '#ff3b30', '#4cd964', '#007aff', '#ff9500', '#ffcc00', '#000000',
  ]);

  // extended palette for the popup grid
  const extended = useMemo(() => ([
    '#ff3b30','#ff5e57','#ff7f50','#ff9500','#ffcc00','#ffd60a',
    '#4cd964','#34c759','#28cd41','#007aff','#0a84ff','#64d2ff',
    '#5856d6','#af52de','#ff375f','#8e8e93','#ffffff','#000000',
  ]), []);

  // which primary swatch are we replacing?
  const [replaceIndex, setReplaceIndex] = useState(0);

  // ----- popover state
  const [openMenu, setOpenMenu] = useState(null); // 'eraser'|'pen'|'highlight'|'palette'|null
  const rootRef = useRef(null);

  // click outside to close
  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDocClick, true);
    return () => document.removeEventListener('mousedown', onDocClick, true);
  }, []);

  // helpers to emit changes but keep toolbar robust if parent didn’t pass handlers
  const emitPenWidth       = (w) => onPenWidthChange?.(w);
  const emitHighWidth     = (w) => onHighlightWidthChange?.(w);
  const emitEraserConfig  = (p) => onEraserConfigChange?.(p);
  const emitHighlightMode = (m) => onHighlightModeChange?.(m);
  const emitPalette       = (arr) => onPaletteUpdate?.(arr);
  // --- thickness indicator (dot) ---
  const activeWidth = (
    activeTool === 'pen'       ? penWidth :
    activeTool === 'highlight' ? highlightWidth :
    activeTool === 'eraser'    ? eraserWidth : 8
  );
  // keep the dot visible but representative
  const indicatorSize  = Math.min(28, Math.max(8, Number(activeWidth) || 8));
  const indicatorColor = (activeTool === 'eraser') ? '#111' : currentColor;

  return (
    <div className="toolbar" ref={rootRef}>
      {/* tools + their carets */}
      <div className="tool-group">
        {tools.map((tool) => (
          <div className="tool-with-menu" key={tool.id}>
            <button
              className={`tool-button ${activeTool === tool.id ? 'active' : ''}`}
              onClick={() => { if (!quotePicking) onToolSelect(tool.id); }}
              title={tool.title}
              aria-label={tool.title}
              disabled={quotePicking}
            >
              <span className="material-icons">{tool.icon}</span>
              <span className="tool-label">{tool.label}</span>
            </button>

            <button
              className="tool-caret"
              aria-label={`${tool.label} options`}
              title={`${tool.label} options`}
              onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === tool.id ? null : tool.id); }}
              disabled={quotePicking}
            >
              <span className="material-icons">arrow_drop_down</span>
            </button>

            {/* --- ERASER MENU --- */}
            {tool.id === 'eraser' && openMenu === 'eraser' && (
              <div className="tool-menu">
                <div className="menu-row">
                  <label className="menu-label">Mode</label>
                  <div className="seg">
                    <button
                      className={`seg-btn ${eraserStyle === 'erase' ? 'on' : ''}`}
                      onClick={() => emitEraserConfig?.({ style: 'erase' })}
                    >Pixel</button>
                    <button
                      className={`seg-btn ${eraserStyle === 'strike' ? 'on' : ''}`}
                      onClick={() => emitEraserConfig?.({ style: 'strike' })}
                    >Strike</button>
                  </div>
                </div>

                <div className="menu-row">
                  <label className="menu-label">Thickness</label>
                  <input type="range" min={4} max={40} value={eraserWidth}
                        onChange={(e) => emitEraserConfig?.({ width: Number(e.target.value) })}/>
                  <span aria-hidden="true"
                        style={{ display:'inline-block', margin:'0 8px',
                                width:Math.min(22, Math.max(8, Number(eraserWidth)||8)),
                                height:Math.min(22, Math.max(8, Number(eraserWidth)||8)),
                                borderRadius:9999, background:'#111',
                                boxShadow:'0 0 0 2px #fff, 0 0 0 1px rgba(0,0,0,.18)' }}/>
                  <span className="menu-value">{eraserWidth}px</span>
                </div>
              </div>
            )}

            {/* --- PEN MENU --- */}
            {tool.id === 'pen' && openMenu === 'pen' && (
              <div className="tool-menu">
                <div className="menu-row">
                  <label className="menu-label">Thickness</label>
                  <input
                    type="range"
                    min={1}
                    max={20}
                    value={penWidth}
                    onChange={(e) => emitPenWidth(Number(e.target.value))}
                  />
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block',
                      margin: '0 8px',
                      width: Math.min(22, Math.max(8, Number(penWidth) || 8)),
                      height: Math.min(22, Math.max(8, Number(penWidth) || 8)),
                      borderRadius: 9999,
                      background: currentColor,
                      boxShadow: '0 0 0 2px #fff, 0 0 0 1px rgba(0,0,0,.18)',
                    }}
                  />
                  <span className="menu-value">{penWidth}px</span>
                </div>
              </div>
            )}
            {/* --- HIGHLIGHT MENU --- */}
            {tool.id === 'highlight'&& openMenu === 'highlight'&& (
              <div className="tool-menu">
                <div className="menu-row">
                  <label className="menu-label">Mode</label>
                  <div className="seg">
                    <button
                      className={`seg-btn ${highlightMode === 'freehand' ? 'on' : ''}`}
                      onClick={() => emitHighlightMode('freehand')}
                    >Freehand</button>
                    <button
                      className={`seg-btn ${highlightMode === 'text' ? 'on' : ''}`}
                      onClick={() => emitHighlightMode('text')}
                    >Text select</button>
                  </div>
                </div>

                <div className="menu-row">
                  <label className="menu-label">Thickness</label>
                  <input
                    type="range"
                    min={6}
                    max={48}
                    value={highlightWidth}
                    onChange={(e) => emitHighWidth(Number(e.target.value))}
                  />
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block',
                      margin: '0 8px',
                      width: Math.min(22, Math.max(8, Number(highlightWidth) || 8)),
                      height: Math.min(22, Math.max(8, Number(highlightWidth) || 8)),
                      borderRadius: 9999,
                      background: currentColor,
                      opacity: 0.5,                  /* looks like a highlighter */
                      boxShadow: '0 0 0 2px #fff, 0 0 0 1px rgba(0,0,0,.18)',
                    }}
                  />
                  <span className="menu-value">{highlightWidth}px</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Undo / Redo */}
      <div className="tool-group">
        <button
          className="tool-button"
          onClick={() => !quotePicking && onUndo?.()}
          disabled={quotePicking || !canUndo}
          title="Undo"
          aria-label="Undo"
        >
          <span className="material-icons">undo</span>
          <span className="tool-label">Undo</span>
        </button>

        <button
          className="tool-button"
          onClick={() => !quotePicking && onRedo?.()}
          disabled={quotePicking || !canRedo}
          title="Redo"
          aria-label="Redo"
        >
          <span className="material-icons">redo</span>
          <span className="tool-label">Redo</span>
        </button>
      </div>

      {/* palette + caret */}
      <div className="color-picker">
        <span className="color-picker-label">Color:</span>

        <div className="color-options">
          {palette.map((hex, i) => (
            <button
              key={hex + i}
              className={`color-option ${currentColor === hex ? 'selected' : ''}`}
              style={{ backgroundColor: hex }}
              onClick={() => setCurrentColor(hex)}
              title={hex}
              aria-label={hex}
            />
          ))}
        </div>

        {/* ANCHOR just the caret + menu so positioning is relative to this button */}
        <div className={`tool-with-menu ${openMenu === 'palette' ? 'open' : ''}`}>
          <button
            className="tool-caret"
            aria-label="Palette options"
            title="Palette options"
            onClick={(e) => {
              e.stopPropagation();
              setOpenMenu(openMenu === 'palette' ? null : 'palette');
            }}
            disabled={quotePicking}
          >
            <span className="material-icons">arrow_drop_down</span>
          </button>

          {openMenu === 'palette' && (
            <div className="tool-menu palette-menu">
              <div className="menu-row">
                <label className="menu-label">Replace</label>
                <div className="palette-primary">
                  {palette.map((hex, idx) => (
                    <button
                      key={'p' + idx}
                      className={`color-option ${replaceIndex === idx ? 'replacing' : ''}`}
                      style={{ backgroundColor: hex }}
                      onClick={() => setReplaceIndex(idx)}
                      aria-label={`Pick swatch ${idx + 1} to replace`}
                      title="Pick swatch to replace"
                    />
                  ))}
                </div>
              </div>

              <div className="menu-row">
                <label className="menu-label">With</label>
                <div className="palette-grid">
                  {extended.map((hex) => (
                    <button
                      key={hex}
                      className="color-option"
                      style={{ backgroundColor: hex }}
                      onClick={() => {
                        const next = palette.map((c, i) => (i === replaceIndex ? hex : c));
                        setPalette(next);
                        onPaletteUpdate?.(next);
                        if (currentColor === palette[replaceIndex]) setCurrentColor(hex);
                      }}
                      aria-label={hex}
                      title={hex}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
